import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Package, Save, ArrowLeft, Tag, IndianRupee, Boxes, Image as ImageIcon,
  Plus, Trash2, Upload, RefreshCw, AlertCircle, CheckCircle, Info, Loader2,
  Lock, KeyRound
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import api, { productsAPI, brandsAPI, categoriesAPI, taxAPI, uomAPI, vendorsAPI, securityAPI } from '@/services/api';
import { WeightHint } from '@/components/hq/WeightHint';

const round2 = (v) => Math.round((parseFloat(v) || 0) * 100) / 100;
const round1 = (v) => Math.round((parseFloat(v) || 0) * 10) / 10;
// Price incl. tax for a level mode. markdown -> off MRP (incl tax), markup -> on base cost (excl tax)
const priceForModeCalc = (mode, value, mrp, baseCost, rate) => {
  const v = parseFloat(value) || 0;
  const m = parseFloat(mrp) || 0;
  const c = parseFloat(baseCost) || 0;
  const g = parseFloat(rate) || 0;
  if (mode === 'markdown') return m > 0 ? round2(m * (1 - v / 100)) : 0;
  if (mode === 'percentage' || mode === 'markup') {
    const excl = c * (1 + v / 100);
    return c > 0 ? round2(g > 0 ? excl * (1 + g / 100) : excl) : 0;
  }
  if (mode === 'flat') return round2(v);
  return 0;
};
const money = (v) => `₹${(parseFloat(v) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const IMAGE_MIME_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const IMAGE_ACCEPT = '.jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp';
const IMAGE_MAX_BYTES = 3 * 1024 * 1024;


const ProductEditPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isEdit = !!id;

  // Loading states
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingDropdowns, setLoadingDropdowns] = useState(true);

  // Dropdown data
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [taxGroups, setTaxGroups] = useState([]);
  const [uoms, setUoms] = useState([]);
  const [vendors, setVendors] = useState([]);

  // Tab state
  const [activeTab, setActiveTab] = useState(
    new URLSearchParams(window.location.search).get('tab') || 'general'
  );

  // Form data - General
  const [generalData, setGeneralData] = useState({
    sku: '',
    name: '',
    description: '',
    brand_id: '',
    category_id: '',
    main_category_id: '',
    sub_category_id: '',
    hsn_code: '',
    uom_id: '',
    base_uom: '',
    purchase_uom: '',
    uom_conversion_factor: 1,
    default_vendor_id: '',
    supplier_ids: [],
    primary_supplier_id: '',
    barcodes: [],
    has_expiry: false,
    expiry_alert_days: 30,
    ecom_enabled: false,
    online_stock_percent: 100,
    weight_grams: 0,
    length_cm: 0,
    width_cm: 0,
    height_cm: 0
  });

  // Form data - Pricing
  const [pricingData, setPricingData] = useState({
    tax_group_id: '',
    mrp: 0,
    cost_price: 0,
    base_cost: 0,
    usd_base_cost: 0,
    markup_type: 'markdown', // 'markdown' (default), 'percentage' (markup), 'flat'
    markup_value: 0,
    selling_price: 0,
    // Multi-level pricing
    price_levels: [
      { level: 1, name: 'Default', type: 'auto', value: 0, price: 0, enabled: true },
      { level: 2, name: 'Wholesale', type: 'markdown', value: 0, price: 0, enabled: false },
      { level: 3, name: 'Dealer', type: 'markdown', value: 0, price: 0, enabled: false },
      { level: 4, name: 'Franchise', type: 'markdown', value: 0, price: 0, enabled: false },
      { level: 5, name: 'Special', type: 'markdown', value: 0, price: 0, enabled: false }
    ]
  });

  // Form data - Inventory
  const [inventoryData, setInventoryData] = useState({
    min_stock_level: 0,
    max_stock_level: 0,
    reorder_level: 0,
    low_stock_alert_qty: 0
  });

  // Form data - Images
  const [imagesData, setImagesData] = useState({
    primary_image: '',
    additional_images: []
  });
  
  // Image upload state
  const [catChain, setCatChain] = useState([]);
  const [duplicateProduct, setDuplicateProduct] = useState(null);
  const [pricingLoadedAt, setPricingLoadedAt] = useState(0);
  const [pricingLockedSince, setPricingLockedSince] = useState(null);
  const [pricingUnlockCode, setPricingUnlockCode] = useState('');
  const [unlockDialogOpen, setUnlockDialogOpen] = useState(false);
  const [pricingUnlocked, setPricingUnlocked] = useState(false);
  const [unlocking, setUnlocking] = useState(false);

  // Snapshot of pricing at load time — used to detect if pricing actually changed
  // so we only call setPrice (and trigger the Super Admin code gate) on real pricing edits.
  const originalPricingRef = useRef(null);
  const getPricingFingerprint = (data) => JSON.stringify({
    mrp: Number(data.mrp) || 0,
    cost_price: Number(data.cost_price) || 0,
    base_cost: Number(data.base_cost) || 0,
    usd_base_cost: Number(data.usd_base_cost) || 0,
    markup_type: data.markup_type || '',
    markup_value: Number(data.markup_value) || 0,
    selling_price: Number(data.selling_price) || 0,
    tax_group_id: data.tax_group_id || '',
    price_levels: (data.price_levels || []).map(l => ({
      level: l.level,
      name: l.name,
      type: l.type,
      value: Number(l.value) || 0,
      price: Number(l.price) || 0,
      enabled: !!l.enabled
    }))
  });

  // Pricing/margin freezes 1 hour after it was last saved
  const isPricingFrozen = Boolean(
    pricingLockedSince && !pricingUnlocked &&
    Date.now() - new Date(pricingLockedSince).getTime() > 60 * 60 * 1000
  );

  // MRP Mark Down % (Optional helper next to MRP; not persisted, just drives Basic Cost auto-fill)
  const [mrpMarkdownPct, setMrpMarkdownPct] = useState('');
  const [uploadingPrimary, setUploadingPrimary] = useState(false);
  const [uploadingAdditional, setUploadingAdditional] = useState(false);
  const primaryImageInputRef = useRef(null);
  const additionalImageInputRef = useRef(null);

  const childCategories = (parentId) => (parentId ? categories.filter(c => c.parent_id === parentId) : []);

  // Selected category path (main -> sub -> sub-sub ...)
  const selectCategoryAt = (depth, value) => {
    const chain = [...catChain.slice(0, depth), value];
    setCatChain(chain);
    const leaf = chain[chain.length - 1];
    setGeneralData(prev => ({
      ...prev,
      category_id: leaf || '',
      main_category_id: chain[0] || '',
      sub_category_id: chain.length > 1 ? chain[chain.length - 1] : ''
    }));
  };

  // Validate an image file: JPG/JPEG/PNG/WEBP, max 3MB
  const validateImageFile = (file) => {
    if (!IMAGE_MIME_TYPES.includes((file.type || '').toLowerCase())) {
      toast({
        title: 'Unsupported file',
        description: `${file.name}: only JPG, JPEG, PNG and WEBP images are allowed`,
        variant: 'destructive'
      });
      return false;
    }
    if (file.size > IMAGE_MAX_BYTES) {
      toast({
        title: 'File too large',
        description: `${file.name}: each image must be 3MB or less`,
        variant: 'destructive'
      });
      return false;
    }
    return true;
  };

  // Handle primary image file upload
  const handlePrimaryImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!validateImageFile(file)) {
      if (primaryImageInputRef.current) primaryImageInputRef.current.value = '';
      return;
    }
    
    try {
      setUploadingPrimary(true);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('is_primary', 'true');
      
      // Use product ID if editing, or 'temp' for new products
      const productId = id || 'temp';
      
      const response = await api.post(`/assets/upload/product/${productId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data?.url) {
        setImagesData(prev => ({ ...prev, primary_image: response.data.url }));
        toast({ title: 'Success', description: 'Primary image uploaded successfully' });
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      toast({ 
        title: 'Upload Failed', 
        description: error.response?.data?.detail || 'Failed to upload image. Please try again.', 
        variant: 'destructive' 
      });
    } finally {
      setUploadingPrimary(false);
      // Reset file input
      if (primaryImageInputRef.current) {
        primaryImageInputRef.current.value = '';
      }
    }
  };
  
  // Handle additional image file upload (supports multiple files)
  const handleAdditionalImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    const productId = id || 'temp';
    setUploadingAdditional(true);
    
    for (const file of files) {
      if (!validateImageFile(file)) continue;
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('is_primary', 'false');
        
        const response = await api.post(`/assets/upload/product/${productId}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        if (response.data?.url) {
          const uploadedUrl = response.data.url;
          setImagesData(prev => ({ 
            ...prev, 
            additional_images: [...prev.additional_images, uploadedUrl] 
          }));
        }
      } catch (error) {
        console.error('Error uploading image:', error);
        toast({ 
          title: 'Upload Failed', 
          description: error.response?.data?.detail || `Failed to upload ${file.name}`, 
          variant: 'destructive' 
        });
      }
    }
    
    setUploadingAdditional(false);
    toast({ title: 'Upload complete', description: 'Images uploaded successfully' });
    if (additionalImageInputRef.current) {
      additionalImageInputRef.current.value = '';
    }
  };

  // Load dropdowns
  const loadDropdowns = useCallback(async () => {
    try {
      setLoadingDropdowns(true);
      const [brandsRes, catsRes, taxRes, uomRes, vendorsRes] = await Promise.all([
        brandsAPI.list({ is_active: true }),
        categoriesAPI.list({ is_active: true }),
        taxAPI.list({ is_active: true }),
        uomAPI.list({ is_active: true }),
        vendorsAPI.list({ is_active: true })
      ]);
      setBrands(brandsRes.data?.items || brandsRes.data || []);
      setCategories(catsRes.data?.items || catsRes.data || []);
      setTaxGroups(taxRes.data?.items || taxRes.data || []);
      setUoms(uomRes.data?.items || uomRes.data || []);
      setVendors(vendorsRes.data?.items || vendorsRes.data || []);
    } catch (error) {
      console.error('Error loading dropdowns:', error);
      toast({ title: 'Error', description: 'Failed to load form data', variant: 'destructive' });
    } finally {
      setLoadingDropdowns(false);
    }
  }, [toast]);

  // Load product data for edit
  const loadProduct = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const response = await productsAPI.get(id);
      const product = response.data;

      // Set general data
      setGeneralData({
        sku: product.sku || '',
        name: product.name || '',
        description: product.description || '',
        brand_id: product.brand_id || '',
        category_id: product.category_id || '',
        main_category_id: product.main_category_id || '',
        sub_category_id: product.sub_category_id || '',
        hsn_code: product.hsn_code || '',
        uom_id: product.uom_id || '',
        base_uom: product.base_uom || '',
        purchase_uom: product.purchase_uom || '',
        uom_conversion_factor: product.uom_conversion_factor || 1,
        default_vendor_id: product.default_vendor_id || '',
        supplier_ids: product.supplier_ids || [],
        primary_supplier_id: product.primary_supplier_id || '',
        barcodes: product.barcodes || [],
        has_expiry: product.has_expiry || false,
        expiry_alert_days: product.expiry_alert_days || 30,
        ecom_enabled: product.ecom_enabled || false,
        online_stock_percent: product.online_stock_percent || 100,
        weight_grams: product.weight_grams || 0,
        length_cm: product.length_cm || 0,
        width_cm: product.width_cm || 0,
        height_cm: product.height_cm || 0
      });

      // Try to load pricing data from product_prices collection
      let loadedPricing = null;
      try {
        const priceResponse = await productsAPI.getPrice(id);
        const price = priceResponse.data;
        if (price) {
          const sellingPrice = price.selling_price || 0;
          const savedSlabs = price.price_slabs || [];
          const fromMethod = (m) => (m === 'markdown' ? 'markdown' : m === 'flat' ? 'flat' : 'percentage');
          setPricingData(prev => {
            const nextLevels = prev.price_levels.map(level => {
              const slab = savedSlabs.find((s, i) => (s.level || i + 1) === level.level);
              if (level.level === 1) {
                return { ...level, price: slab?.selling_price || sellingPrice };
              }
              if (!slab) return { ...level, enabled: false };
              return {
                ...level,
                name: slab.name || level.name,
                enabled: true,
                type: fromMethod(slab.pricing_method),
                value: slab.percentage || 0,
                price: slab.selling_price || 0
              };
            });
            const nextMarkupType = (() => {
              const slabMethod = savedSlabs.find((sl, i) => (sl.level || i + 1) === 1)?.pricing_method;
              if (slabMethod) return fromMethod(slabMethod);
              if (price.margin_type === 'markdown' || price.margin_type === 'flat') return price.margin_type;
              if (price.pricing_method) return fromMethod(price.pricing_method);
              return 'percentage';
            })();
            const nextMarkupValue = (() => {
              const slab1 = savedSlabs.find((sl, i) => (sl.level || i + 1) === 1);
              if (slab1 && slab1.percentage !== undefined && slab1.percentage !== null) return slab1.percentage;
              return price.margin_value || 0;
            })();
            loadedPricing = {
              ...prev,
              tax_group_id: product.tax_group_id || '',
              mrp: price.mrp || 0,
              cost_price: price.cost_price || price.basic_cost || 0,
              base_cost: price.base_cost || price.basic_cost || price.cost_price || 0,
              usd_base_cost: price.usd_base_cost || 0,
              markup_type: nextMarkupType,
              markup_value: nextMarkupValue,
              selling_price: sellingPrice,
              price_levels: nextLevels
            };
            return loadedPricing;
          });
        }
      } catch (priceError) {
        // No price found, use defaults from product
        const sellingPrice = product.selling_price || product.mrp || 0;
        setPricingData(prev => {
          loadedPricing = {
            ...prev,
            tax_group_id: product.tax_group_id || '',
            mrp: product.mrp || 0,
            cost_price: product.cost_price || 0,
            base_cost: product.base_cost || product.cost_price || 0,
            selling_price: sellingPrice,
            price_levels: prev.price_levels.map(level =>
              level.level === 1 ? { ...level, price: sellingPrice } : level
            )
          };
          return loadedPricing;
        });
      }
      // Snapshot pricing so we can detect real edits at save-time
      if (loadedPricing) {
        originalPricingRef.current = getPricingFingerprint(loadedPricing);
      }
      // Let the derived-price effects re-run now that the stored pricing has landed
      setPricingLockedSince(
        product.pricing_updated_at || product.current_price?.created_at || null
      );
      setPricingLoadedAt(Date.now());

      // Set inventory data
      setInventoryData({
        min_stock_level: product.min_stock_level || 0,
        max_stock_level: product.max_stock_level || 0,
        reorder_level: product.reorder_level || 0,
        low_stock_alert_qty: product.low_stock_alert_qty || 0
      });

      // Set images data
      setImagesData({
        primary_image: product.primary_image || '',
        additional_images: product.additional_images || product.images || []
      });

    } catch (error) {
      console.error('Error loading product:', error);
      toast({ title: 'Error', description: 'Failed to load product', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [id, toast]);

  // Generate next SKU
  const generateSku = async () => {
    try {
      const result = await productsAPI.getNextSku();
      setGeneralData(prev => ({ ...prev, sku: result.sku }));
    } catch (error) {
      console.error('Error generating SKU:', error);
    }
  };

  useEffect(() => {
    loadDropdowns();
    if (isEdit) {
      loadProduct();
    } else {
      // Auto-generate SKU for new products
      generateSku();
    }
  }, [loadDropdowns, loadProduct, isEdit]);

  // Warn as soon as the name (within the brand) matches an existing product
  useEffect(() => {
    const name = (generalData.name || '').trim();
    if (name.length < 3) {
      setDuplicateProduct(null);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await api.get('/products/check-duplicate', {
          params: {
            name,
            brand_id: generalData.brand_id || undefined,
            exclude_id: id || undefined
          }
        });
        setDuplicateProduct(res.data?.is_duplicate ? res.data.product : null);
      } catch (e) {
        setDuplicateProduct(null);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [generalData.name, generalData.brand_id, id]);

  // Legacy products store only base_uom - resolve the matching UOM id so Save is not blocked
  useEffect(() => {
    if (!uoms.length || generalData.uom_id || !generalData.base_uom) return;
    const code = String(generalData.base_uom).trim().toLowerCase();
    const match = uoms.find(u =>
      [u.code, u.symbol, u.name].filter(Boolean).some(v => String(v).trim().toLowerCase() === code)
    );
    if (match) {
      setGeneralData(prev => ({ ...prev, uom_id: match.id }));
    }
  }, [uoms, generalData.uom_id, generalData.base_uom]);

  // Rebuild the category chain from the saved (leaf) category id
  useEffect(() => {
    if (!categories.length || !generalData.category_id) return;
    const path = [];
    let current = categories.find(c => c.id === generalData.category_id);
    while (current) {
      path.unshift(current.id);
      current = current.parent_id ? categories.find(c => c.id === current.parent_id) : null;
    }
    setCatChain(prev => (prev.join('|') === path.join('|') ? prev : path));
  }, [categories, generalData.category_id]);

  // ---- Pricing maths (all "price" values are INCL. tax, like MRP) ----
  const gstRate = (() => {
    const tg = taxGroups.find(t => t.id === pricingData.tax_group_id);
    return tg ? (tg.total_rate ?? tg.rate ?? 0) : 0;
  })();

  const exclOf = (inclPrice) => (gstRate > 0 ? inclPrice / (1 + gstRate / 100) : inclPrice);
  const inclOf = (exclPrice) => (gstRate > 0 ? exclPrice * (1 + gstRate / 100) : exclPrice);

  const priceForMode = (mode, value) => priceForModeCalc(
    mode,
    value,
    pricingData.mrp,
    parseFloat(pricingData.base_cost) || parseFloat(pricingData.cost_price) || 0,
    gstRate
  );

  const levelMetrics = (priceIncl) => {
    const mrp = parseFloat(pricingData.mrp) || 0;
    const baseCost = parseFloat(pricingData.base_cost) || parseFloat(pricingData.cost_price) || 0;
    const priceExcl = round2(exclOf(priceIncl));
    const profit = round2(priceExcl - baseCost);
    return {
      priceExcl,
      profit,
      marginPct: priceExcl > 0 ? round1((profit / priceExcl) * 100) : 0,
      markupPct: baseCost > 0 ? round1((profit / baseCost) * 100) : 0,
      markdownPct: mrp > 0 ? round1(((mrp - priceIncl) / mrp) * 100) : 0
    };
  };

  // Calculate selling price (incl. tax) from the header markup settings
  const calculateSellingPrice = useCallback((data, rate) => {
    const { mrp, base_cost, cost_price, markup_type, markup_value } = data;
    const baseCost = parseFloat(base_cost) || parseFloat(cost_price) || 0;
    const v = parseFloat(markup_value) || 0;
    let price = parseFloat(mrp) || 0;

    if (markup_type === 'markdown' && v > 0) {
      price = (parseFloat(mrp) || 0) * (1 - v / 100);
    } else if ((markup_type === 'percentage' || markup_type === 'markup') && v > 0) {
      const excl = baseCost * (1 + v / 100);
      price = rate > 0 ? excl * (1 + rate / 100) : excl;
    } else if (markup_type === 'flat' && v > 0) {
      price = v;
    }

    return Math.round(price * 100) / 100;
  }, []);

  // Update Level 1 price when markup changes
  useEffect(() => {
    const tg = taxGroups.find(t => t.id === pricingData.tax_group_id);
    const rate = tg ? (tg.total_rate ?? tg.rate ?? 0) : 0;
    const newPrice = calculateSellingPrice(pricingData, rate);
    if (newPrice !== pricingData.selling_price) {
      setPricingData(prev => ({
        ...prev,
        selling_price: newPrice,
        price_levels: prev.price_levels.map(level => 
          level.level === 1 ? { ...level, price: newPrice } : level
        )
      }));
    }
  }, [pricingData.mrp, pricingData.base_cost, pricingData.cost_price, pricingData.markup_type,
      pricingData.markup_value, pricingData.tax_group_id, taxGroups, pricingLoadedAt, calculateSellingPrice]);

  // Keep levels 2-5 in step with cost / MRP / GST changes
  useEffect(() => {
    const tg = taxGroups.find(t => t.id === pricingData.tax_group_id);
    const rate = tg ? (tg.total_rate ?? tg.rate ?? 0) : 0;
    const baseCost = parseFloat(pricingData.base_cost) || parseFloat(pricingData.cost_price) || 0;

    setPricingData(prev => {
      let changed = false;
      const levels = prev.price_levels.map(level => {
        if (level.level === 1 || !level.enabled) return level;
        const newPrice = priceForModeCalc(level.type, level.value, prev.mrp, baseCost, rate);
        if (newPrice !== level.price) {
          changed = true;
          return { ...level, price: newPrice };
        }
        return level;
      });
      return changed ? { ...prev, price_levels: levels } : prev;
    });
  }, [pricingData.mrp, pricingData.cost_price, pricingData.base_cost, pricingData.tax_group_id, taxGroups, pricingLoadedAt]);

  // Basic Cost drives the Base Cost until a GRN/user overrides it separately
  const updateBasicCost = (value) => {
    const newCost = parseFloat(value) || 0;
    setPricingData(prev => {
      const prevCost = parseFloat(prev.cost_price) || 0;
      const prevBase = parseFloat(prev.base_cost) || 0;
      const keepInSync = prevBase === 0 || prevBase === prevCost;
      return { ...prev, cost_price: newCost, base_cost: keepInSync ? newCost : prev.base_cost };
    });
    // Recalculate MRP Mark Down % to reflect the manually entered basic cost
    const m = parseFloat(pricingData.mrp) || 0;
    if (m > 0 && newCost >= 0) {
      const inclCost = gstRate > 0 ? newCost * (1 + gstRate / 100) : newCost;
      const pct = ((m - inclCost) / m) * 100;
      setMrpMarkdownPct((Math.round(pct * 100) / 100).toString());
    } else {
      setMrpMarkdownPct('');
    }
  };

  // Reset the MRP Mark Down % helper whenever a product's pricing is (re)loaded
  useEffect(() => {
    if (pricingLoadedAt === 0) return;
    const m = parseFloat(pricingData.mrp) || 0;
    const c = parseFloat(pricingData.cost_price) || parseFloat(pricingData.base_cost) || 0;
    if (m > 0 && c > 0) {
      const inclCost = gstRate > 0 ? c * (1 + gstRate / 100) : c;
      const pct = ((m - inclCost) / m) * 100;
      setMrpMarkdownPct(pct >= 0 && pct <= 100 ? (Math.round(pct * 100) / 100).toString() : '');
    } else {
      setMrpMarkdownPct('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricingLoadedAt]);

  // Apply MRP Mark Down % - auto-fill Basic Cost + Base Cost (both Excl. GST)
  const applyMrpMarkdown = (pctVal, mrpOverride) => {
    const m = parseFloat(mrpOverride !== undefined ? mrpOverride : pricingData.mrp) || 0;
    const p = parseFloat(pctVal);
    if (!m || m <= 0 || isNaN(p)) return;
    const netBasicIncl = m - (m * p / 100);
    const derivedBasicCost = gstRate > 0 ? netBasicIncl / (1 + gstRate / 100) : netBasicIncl;
    const rounded = Math.round(derivedBasicCost * 100) / 100;
    if (rounded < 0) return;
    setPricingData(prev => {
      const prevCost = parseFloat(prev.cost_price) || 0;
      const prevBase = parseFloat(prev.base_cost) || 0;
      const keepInSync = prevBase === 0 || prevBase === prevCost;
      return { ...prev, cost_price: rounded, base_cost: keepInSync ? rounded : prev.base_cost };
    });
  };

  // Handlers for the Mark Down % helper next to MRP
  const handleMrpMarkdownPctChange = (val) => {
    setMrpMarkdownPct(val);
    if (val === '' || val === null || val === undefined) return;
    applyMrpMarkdown(val);
  };

  // MRP change: preserve pct-driven auto-fill when a % is already set
  const handleMrpChange = (val) => {
    const newMrp = parseFloat(val) || 0;
    setPricingData(prev => ({ ...prev, mrp: newMrp }));
    if (mrpMarkdownPct !== '' && !isNaN(parseFloat(mrpMarkdownPct))) {
      applyMrpMarkdown(mrpMarkdownPct, newMrp);
    }
  };

  // Update multi-level prices
  const updatePriceLevel = (levelNum, field, value) => {
    setPricingData(prev => {
      const newLevels = prev.price_levels.map(level => {
        if (level.level === levelNum) {
          const updated = { ...level, [field]: value };
          
          // Calculate price (incl. tax) based on the level's own mode
          if (field === 'type' || field === 'value') {
            updated.price = priceForMode(updated.type, updated.value);
          }
          return updated;
        }
        return level;
      });
      return { ...prev, price_levels: newLevels };
    });
  };

  // Add barcode
  const addBarcode = () => {
    setGeneralData(prev => ({
      ...prev,
      barcodes: [...prev.barcodes, { barcode: '', type: 'ean13', is_primary: prev.barcodes.length === 0 }]
    }));
  };

  // Remove barcode
  const removeBarcode = (index) => {
    setGeneralData(prev => ({
      ...prev,
      barcodes: prev.barcodes.filter((_, i) => i !== index)
    }));
  };

  // Update barcode
  const updateBarcode = (index, field, value) => {
    setGeneralData(prev => ({
      ...prev,
      barcodes: prev.barcodes.map((b, i) => i === index ? { ...b, [field]: value } : b)
    }));
  };

  // Save product
  const handleSave = async () => {
    // Validation
    if (!generalData.name.trim()) {
      toast({ title: 'Validation Error', description: 'Product name is required', variant: 'destructive' });
      setActiveTab('general');
      return;
    }
    if (!generalData.category_id) {
      toast({ title: 'Validation Error', description: 'Main category is required', variant: 'destructive' });
      setActiveTab('general');
      return;
    }
    if (duplicateProduct) {
      toast({
        title: 'Duplicate product',
        description: `"${duplicateProduct.name}" (SKU ${duplicateProduct.sku}) already exists. Edit that product instead.`,
        variant: 'destructive'
      });
      setActiveTab('general');
      return;
    }
    if (childCategories(generalData.category_id).length > 0) {
      toast({ title: 'Validation Error', description: 'Please select a sub category for the chosen category', variant: 'destructive' });
      setActiveTab('general');
      return;
    }
    if (!generalData.uom_id && !id) {
      toast({ title: 'Validation Error', description: 'UOM is required', variant: 'destructive' });
      setActiveTab('general');
      return;
    }
    if (!pricingData.tax_group_id) {
      toast({ title: 'Validation Error', description: 'Tax Group is required', variant: 'destructive' });
      setActiveTab('pricing');
      return;
    }
    if (!generalData.hsn_code.trim()) {
      toast({ title: 'Validation Error', description: 'HSN Code is required', variant: 'destructive' });
      setActiveTab('general');
      return;
    }

    try {
      setSaving(true);

      const payload = {
        ...generalData,
        ...pricingData,
        ...inventoryData,
        images: imagesData.additional_images,
        primary_image: imagesData.primary_image
      };

      let productId = id;
      
      if (isEdit) {
        await productsAPI.update(id, payload);
      } else {
        const createResponse = await productsAPI.create(payload);
        productId = createResponse.data.id;
      }
      
      // Save pricing data to product_prices collection
      // ONLY when pricing actually changed vs the load-time snapshot.
      // General / Inventory / Images edits should not trigger the Super Admin code gate.
      let priceSaved = true;
      const currentFingerprint = getPricingFingerprint(pricingData);
      const pricingChanged = !isEdit || originalPricingRef.current !== currentFingerprint;
      if (productId && pricingChanged && (pricingData.mrp > 0 || pricingData.cost_price > 0 || pricingData.base_cost > 0)) {
        try {
          const toMethod = (t) => (t === 'markdown' ? 'markdown' : t === 'flat' ? 'flat' : 'markup');
          const priceSlabs = pricingData.price_levels
            .filter(l => l.level === 1 || l.enabled)
            .map(l => ({
              slab_id: `slab_${l.level}`,
              name: l.name,
              level: l.level,
              percentage: l.level === 1 && l.type === 'auto'
                ? (parseFloat(pricingData.markup_value) || 0)
                : (parseFloat(l.value) || 0),
              selling_price: parseFloat(l.price) || 0,
              is_default: l.level === 1,
              pricing_method: l.level === 1 && l.type === 'auto'
                ? toMethod(pricingData.markup_type)
                : toMethod(l.type)
            }));
          
          const pricePayload = {
            product_id: productId,
            mrp: pricingData.mrp,
            basic_cost: pricingData.cost_price || pricingData.base_cost,
            base_cost: pricingData.base_cost || pricingData.cost_price,
            usd_base_cost: parseFloat(pricingData.usd_base_cost) || 0,
            cost_price: pricingData.cost_price,
            margin_type: pricingData.markup_type === 'percentage' ? 'percentage' : 
                         pricingData.markup_type === 'markdown' ? 'markdown' : 'flat',
            margin_value: pricingData.markup_value,
            selling_price: pricingData.selling_price,
            pricing_method: toMethod(pricingData.markup_type),
            price_slabs: priceSlabs,
            security_code: pricingUnlockCode || null
          };
          const priceRes = await productsAPI.setPrice(productId, pricePayload);
          const syncedStores = priceRes.data?.store_sync?.synced_stores || [];
          if (syncedStores.length) {
            toast({ title: `Price synced with ${syncedStores.length} store${syncedStores.length > 1 ? 's' : ''}`,
              description: `${syncedStores.map(s => s.code || s.name).join(', ')} — POS shows the new price immediately.` });
          }
          // Refresh snapshot so subsequent non-pricing saves don't re-trigger the code gate
          originalPricingRef.current = currentFingerprint;
        } catch (priceError) {
          console.error('Error saving price:', priceError);
          priceSaved = false;
          const detail = priceError.response?.data?.detail;
          if (detail?.error === 'pricing_frozen') {
            // Server froze this product's pricing - ask for the security code and retry
            setPricingLockedSince(detail.locked_since || new Date(Date.now() - 3600001).toISOString());
            setPricingUnlocked(false);
            setUnlockDialogOpen(true);
            toast({
              title: 'Pricing is frozen',
              description: 'Enter the Super Admin security code to unlock, then save again.',
              variant: 'destructive'
            });
            setSaving(false);
            return;
          }
        }
      }
      
      if (priceSaved) {
        toast({ title: 'Success', description: isEdit ? 'Product updated successfully' : 'Product created successfully' });
      } else {
        toast({ 
          title: 'Partial Success', 
          description: 'Product saved but pricing data failed to save. Please edit and try again.', 
          variant: 'warning' 
        });
      }
      
      if (!isEdit) {
        navigate('/hq/products');
      }
    } catch (error) {
      console.error('Error saving product:', error);
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to save product', 
        variant: 'destructive' 
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading || loadingDropdowns) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="product-edit-page">
      <Toaster />
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={() => navigate('/hq/products')}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isEdit ? 'Edit Product' : 'Add New Product'}
            </h1>
            <p className="text-sm text-gray-500">
              {isEdit ? `SKU: ${generalData.sku}` : 'Create a new product with pricing and inventory settings'}
            </p>
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving} className="gap-2" data-testid="save-product-button">
          {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? 'Saving...' : 'Save Product'}
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4 bg-gray-100">
          <TabsTrigger value="general" className="gap-2" data-testid="tab-general">
            <Tag className="w-4 h-4" /> GENERAL
          </TabsTrigger>
          <TabsTrigger value="pricing" className="gap-2" data-testid="tab-pricing">
            <IndianRupee className="w-4 h-4" /> PRICING
          </TabsTrigger>
          <TabsTrigger value="inventory" className="gap-2" data-testid="tab-inventory">
            <Boxes className="w-4 h-4" /> INVENTORY
          </TabsTrigger>
          <TabsTrigger value="images" className="gap-2" data-testid="tab-images">
            <ImageIcon className="w-4 h-4" /> IMAGES
          </TabsTrigger>
        </TabsList>

        {/* GENERAL Tab */}
        <TabsContent value="general" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Basic Info Card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Basic Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>SKU / Code</Label>
                    <div className="flex gap-2">
                      <Input
                        value={generalData.sku}
                        onChange={(e) => setGeneralData(prev => ({ ...prev, sku: e.target.value }))}
                        placeholder="Auto-generated"
                        data-testid="input-sku"
                      />
                      {!isEdit && (
                        <Button variant="outline" size="icon" onClick={generateSku} title="Generate SKU">
                          <RefreshCw className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                  <div>
                    <Label>HSN Code *</Label>
                    <Input
                      value={generalData.hsn_code}
                      onChange={(e) => setGeneralData(prev => ({ ...prev, hsn_code: e.target.value }))}
                      placeholder="Enter HSN code"
                      data-testid="input-hsn"
                    />
                  </div>
                </div>

                <div>
                  <Label>Product Name *</Label>
                  <Input
                    value={generalData.name}
                    onChange={(e) => setGeneralData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Enter product name"
                    className={duplicateProduct ? 'border-red-400 focus-visible:ring-red-400' : ''}
                    data-testid="input-name"
                  />
                  {duplicateProduct && (
                    <div
                      className="mt-2 flex items-start gap-2 p-2 rounded-md bg-red-50 border border-red-200 text-sm text-red-700"
                      data-testid="duplicate-product-warning"
                    >
                      <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>
                        This product already exists: <strong>{duplicateProduct.name}</strong> (SKU {duplicateProduct.sku}).
                        {' '}
                        <button
                          type="button"
                          className="underline font-medium"
                          onClick={() => navigate(`/hq/products/${duplicateProduct.id}/edit`)}
                          data-testid="open-duplicate-product-btn"
                        >
                          Open the existing product
                        </button>{' '}
                        instead of creating a duplicate.
                      </span>
                    </div>
                  )}
                </div>

                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={generalData.description}
                    onChange={(e) => setGeneralData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Product description"
                    rows={3}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Brand</Label>
                    <Select 
                      value={generalData.brand_id || "none"} 
                      onValueChange={(v) => setGeneralData(prev => ({ ...prev, brand_id: v === "none" ? "" : v }))}
                    >
                      <SelectTrigger data-testid="select-brand">
                        <SelectValue placeholder="Select brand" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Brand</SelectItem>
                        {brands.map(b => (
                          <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Main Category *</Label>
                    <Select
                      value={catChain[0] || ''}
                      onValueChange={(v) => selectCategoryAt(0, v)}
                    >
                      <SelectTrigger data-testid="select-category">
                        <SelectValue placeholder="Select main category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.filter(c => !c.parent_id).map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Sub-category levels appear only when the selected category has children */}
                {catChain.map((selected, depth) => {
                  const children = childCategories(selected);
                  if (depth === 0 && !children.length) return null;
                  if (!children.length) return null;
                  const nextSelected = catChain[depth + 1] || '';
                  return (
                    <div key={`cat-level-${depth + 1}`}>
                      <Label>{depth === 0 ? 'Sub Category *' : `Sub Category Level ${depth + 1} *`}</Label>
                      <Select
                        value={nextSelected}
                        onValueChange={(v) => selectCategoryAt(depth + 1, v)}
                      >
                        <SelectTrigger data-testid={`select-sub-category-${depth + 1}`}>
                          <SelectValue placeholder="Select sub category" />
                        </SelectTrigger>
                        <SelectContent>
                          {children.map(c => (
                            <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-500 mt-1">
                        {categories.find(c => c.id === selected)?.name} has sub categories &mdash; pick one.
                      </p>
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            {/* UOM & Vendor Card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Unit of Measure & Supplier</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Sales UOM *</Label>
                    <Select 
                      value={generalData.uom_id} 
                      onValueChange={(v) => {
                        const uom = uoms.find(u => u.id === v);
                        setGeneralData(prev => ({ 
                          ...prev, 
                          uom_id: v,
                          base_uom: uom?.code || ''
                        }));
                      }}
                    >
                      <SelectTrigger data-testid="select-uom">
                        <SelectValue placeholder="Select UOM" />
                      </SelectTrigger>
                      <SelectContent>
                        {uoms.map(u => (
                          <SelectItem key={u.id} value={u.id}>{u.name} ({u.code})</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Purchase UOM</Label>
                    <Input
                      value={generalData.purchase_uom}
                      onChange={(e) => setGeneralData(prev => ({ ...prev, purchase_uom: e.target.value }))}
                      placeholder="e.g., CTN, BOX"
                    />
                  </div>
                </div>

                <div>
                  <Label>Conversion Factor</Label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">1 {generalData.purchase_uom || 'Purchase UOM'} =</span>
                    <Input
                      type="number"
                      value={generalData.uom_conversion_factor}
                      onChange={(e) => setGeneralData(prev => ({ ...prev, uom_conversion_factor: parseFloat(e.target.value) || 1 }))}
                      className="w-24"
                    />
                    <span className="text-sm text-gray-500">{generalData.base_uom || 'Sales UOM'}</span>
                  </div>
                </div>

                <div>
                  <Label>Primary Supplier</Label>
                  <Select 
                    value={generalData.primary_supplier_id || "none"} 
                    onValueChange={(v) => setGeneralData(prev => ({ ...prev, primary_supplier_id: v === "none" ? "" : v, default_vendor_id: v === "none" ? "" : v }))}
                  >
                    <SelectTrigger data-testid="select-vendor">
                      <SelectValue placeholder="Select supplier" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Supplier</SelectItem>
                      {vendors.map(v => (
                        <SelectItem key={v.id} value={v.id}>{v.name} ({v.code})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Barcodes */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label>Barcodes</Label>
                    <Button variant="outline" size="sm" onClick={addBarcode} className="gap-1">
                      <Plus className="w-3 h-3" /> Add
                    </Button>
                  </div>
                  {generalData.barcodes.length === 0 ? (
                    <p className="text-sm text-gray-400">No barcodes added</p>
                  ) : (
                    <div className="space-y-2">
                      {generalData.barcodes.map((bc, i) => (
                        <div key={i} className="flex gap-2 items-center">
                          <Input
                            value={bc.barcode}
                            onChange={(e) => updateBarcode(i, 'barcode', e.target.value)}
                            placeholder="Barcode value"
                            className="flex-1"
                          />
                          <Select value={bc.type} onValueChange={(v) => updateBarcode(i, 'type', v)}>
                            <SelectTrigger className="w-28">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="ean13">EAN-13</SelectItem>
                              <SelectItem value="ean8">EAN-8</SelectItem>
                              <SelectItem value="upc">UPC</SelectItem>
                              <SelectItem value="custom">Custom</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button variant="ghost" size="icon" onClick={() => removeBarcode(i)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Additional Settings Card */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-lg">Additional Settings</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-sm">Has Expiry</p>
                      <p className="text-xs text-gray-500">Track expiry dates</p>
                    </div>
                    <Switch
                      checked={generalData.has_expiry}
                      onCheckedChange={(c) => setGeneralData(prev => ({ ...prev, has_expiry: c }))}
                    />
                  </div>
                  {generalData.has_expiry && (
                    <div>
                      <Label>Alert Days Before Expiry</Label>
                      <Input
                        type="number"
                        value={generalData.expiry_alert_days}
                        onChange={(e) => setGeneralData(prev => ({ ...prev, expiry_alert_days: parseInt(e.target.value) || 30 }))}
                      />
                    </div>
                  )}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-sm">E-commerce</p>
                      <p className="text-xs text-gray-500">Enable online sales</p>
                    </div>
                    <Switch
                      checked={generalData.ecom_enabled}
                      onCheckedChange={(c) => setGeneralData(prev => ({ ...prev, ecom_enabled: c }))}
                    />
                  </div>
                  {generalData.ecom_enabled && (
                    <div>
                      <Label>Online Stock %</Label>
                      <Input
                        type="number"
                        value={generalData.online_stock_percent}
                        onChange={(e) => setGeneralData(prev => ({ ...prev, online_stock_percent: parseInt(e.target.value) || 100 }))}
                        max={100}
                        min={0}
                      />
                    </div>
                  )}
                </div>
                
                {/* Dimensions */}
                <div className="mt-6">
                  <Label className="mb-2 block">Dimensions & Weight (for shipping)</Label>
                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <Label className="text-xs text-gray-500">Weight (g)</Label>
                      <Input
                        type="number"
                        value={generalData.weight_grams}
                        onChange={(e) => setGeneralData(prev => ({ ...prev, weight_grams: parseFloat(e.target.value) || 0 }))}
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Length (cm)</Label>
                      <Input
                        type="number"
                        value={generalData.length_cm}
                        onChange={(e) => setGeneralData(prev => ({ ...prev, length_cm: parseFloat(e.target.value) || 0 }))}
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Width (cm)</Label>
                      <Input
                        type="number"
                        value={generalData.width_cm}
                        onChange={(e) => setGeneralData(prev => ({ ...prev, width_cm: parseFloat(e.target.value) || 0 }))}
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Height (cm)</Label>
                      <Input
                        type="number"
                        value={generalData.height_cm}
                        onChange={(e) => setGeneralData(prev => ({ ...prev, height_cm: parseFloat(e.target.value) || 0 }))}
                      />
                    </div>
                  </div>
                  <WeightHint grams={generalData.weight_grams} length={generalData.length_cm} width={generalData.width_cm} height={generalData.height_cm} />
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* PRICING Tab */}
        <TabsContent value="pricing" className="mt-6 space-y-6">
          {isPricingFrozen && (
            <div className="rounded-xl border-2 border-red-300 bg-red-50 p-4 flex flex-wrap items-center gap-4" data-testid="pricing-frozen-banner">
              <Lock className="w-5 h-5 text-red-600" />
              <div className="flex-1 min-w-[240px]">
                <p className="font-semibold text-red-800">Pricing is frozen</p>
                <p className="text-sm text-red-700">
                  Saved on {new Date(pricingLockedSince).toLocaleString('en-IN')} - pricing and margin lock 1 hour
                  after they are saved. A Super Admin security code is needed to change them.
                </p>
              </div>
              <Button
                onClick={() => setUnlockDialogOpen(true)}
                className="bg-red-600 hover:bg-red-700"
                data-testid="unlock-pricing-btn"
              >
                <KeyRound className="w-4 h-4 mr-2" /> Unlock with security code
              </Button>
            </div>
          )}
          {pricingUnlocked && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800" data-testid="pricing-unlocked-banner">
              Pricing unlocked for this edit - the change will be recorded in the margin change log.
            </div>
          )}
          <fieldset disabled={isPricingFrozen} className={isPricingFrozen ? 'opacity-60 space-y-6' : 'space-y-6'}>
          {/* Cost & Tax inputs */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Cost, Tax & Selling Price</CardTitle>
              <CardDescription>
                Level 1 price is auto-calculated from the pricing mode below. All prices shown both excl. and incl. GST.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <div>
                  <Label>Tax Group *</Label>
                  <Select
                    value={pricingData.tax_group_id}
                    onValueChange={(v) => setPricingData(prev => ({ ...prev, tax_group_id: v }))}
                  >
                    <SelectTrigger data-testid="select-tax-group">
                      <SelectValue placeholder="Select tax group" />
                    </SelectTrigger>
                    <SelectContent>
                      {taxGroups.map(t => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.name} ({t.total_rate ?? t.rate}%)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">GST: {gstRate}%</p>
                </div>
                <div>
                  <Label>MRP * (Incl. GST)</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                    <Input
                      type="number"
                      value={pricingData.mrp}
                      onChange={(e) => handleMrpChange(e.target.value)}
                      className="pl-8"
                      data-testid="input-mrp"
                    />
                  </div>
                </div>
                <div>
                  <Label>Mode</Label>
                  <Input
                    type="text"
                    value="Mark Down"
                    disabled
                    className="font-medium text-orange-700 bg-orange-50"
                    data-testid="input-mrp-mode"
                  />
                  <p className="text-xs text-gray-500 mt-1">From MRP</p>
                </div>
                <div>
                  <Label>% (Optional)</Label>
                  <div className="relative">
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={mrpMarkdownPct}
                      onChange={(e) => handleMrpMarkdownPctChange(e.target.value)}
                      placeholder="e.g. 20"
                      className="pr-8"
                      data-testid="input-mrp-markdown-pct"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">%</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Auto-fills Basic Cost</p>
                </div>
                <div>
                  <Label>Basic Cost * (Excl. GST)</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                    <Input
                      type="number"
                      value={pricingData.cost_price}
                      onChange={(e) => updateBasicCost(e.target.value)}
                      className="pl-8"
                      data-testid="input-cost"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Standard purchase cost</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Base Cost (Auto GRN, Excl. GST)</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                    <Input
                      type="number"
                      value={pricingData.base_cost}
                      onChange={(e) => setPricingData(prev => ({ ...prev, base_cost: parseFloat(e.target.value) || 0 }))}
                      className="pl-8"
                      data-testid="input-base-cost"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Auto-updated from the latest GRN. Used as the base for markup pricing.
                  </p>
                </div>
                <div>
                  <Label>Base Cost Incl. GST (Reference)</Label>
                  <div className="h-10 px-3 flex items-center bg-gray-50 border rounded-md text-gray-700" data-testid="base-cost-incl-gst">
                    {money(inclOf(parseFloat(pricingData.base_cost) || parseFloat(pricingData.cost_price) || 0))}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Base Cost + GST {gstRate}% (reference only)</p>
                </div>
                <div>
                  <Label>USD Base Cost (Optional)</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                    <Input
                      type="number"
                      value={pricingData.usd_base_cost}
                      onChange={(e) => setPricingData(prev => ({ ...prev, usd_base_cost: parseFloat(e.target.value) || 0 }))}
                      className="pl-8"
                      data-testid="input-usd-base-cost"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Default unit cost for International POs</p>
                </div>
              </div>

              {/* Pricing mode */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border-t pt-5">
                <div>
                  <Label>Pricing Mode (Level 1)</Label>
                  <Select
                    value={pricingData.markup_type}
                    onValueChange={(v) => setPricingData(prev => ({ ...prev, markup_type: v }))}
                  >
                    <SelectTrigger data-testid="select-markup-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="markdown">% Markdown from MRP</SelectItem>
                      <SelectItem value="percentage">% Markup on Base Cost</SelectItem>
                      <SelectItem value="flat">Flat Price (Incl. GST)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>
                    {pricingData.markup_type === 'markdown' ? 'Markdown % (off MRP)'
                      : pricingData.markup_type === 'flat' ? 'Flat Price (Incl. GST)'
                      : 'Markup % (from Base Cost)'}
                  </Label>
                  <div className="relative">
                    {pricingData.markup_type === 'flat'
                      ? <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                      : <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">%</span>}
                    <Input
                      type="number"
                      value={pricingData.markup_value}
                      onChange={(e) => setPricingData(prev => ({ ...prev, markup_value: parseFloat(e.target.value) || 0 }))}
                      className={pricingData.markup_type === 'flat' ? 'pl-8' : 'pr-8'}
                      data-testid="input-markup-value"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {pricingData.markup_type === 'markdown'
                      ? `MRP: ${money(pricingData.mrp)}`
                      : `Base: ${money(pricingData.base_cost || pricingData.cost_price)}`}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                    <p className="text-xs text-emerald-700">Selling Price (Excl GST)</p>
                    <p className="text-lg font-bold text-emerald-700" data-testid="selling-price-excl">
                      {money(exclOf(pricingData.selling_price))}
                    </p>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-xs text-blue-700">Selling Price (Incl GST)</p>
                    <p className="text-lg font-bold text-blue-700" data-testid="calculated-selling-price">
                      {money(pricingData.selling_price)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Margin from MRP - reference block */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="margin-from-mrp">
                <p className="text-sm font-semibold text-amber-800 mb-3">MARGIN FROM MRP (reference)</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {(() => {
                    const mrp = parseFloat(pricingData.mrp) || 0;
                    const basicCost = parseFloat(pricingData.cost_price) || 0;
                    const baseCost = parseFloat(pricingData.base_cost) || basicCost;
                    const mrpExcl = exclOf(mrp);
                    const blocks = [
                      {
                        label: 'MRP vs Basic Cost',
                        diff: mrp - basicCost,
                        pct: mrp > 0 ? ((mrp - basicCost) / mrp) * 100 : 0,
                        testId: 'margin-mrp-basic'
                      },
                      {
                        label: 'MRP vs Base Cost (Auto GRN)',
                        diff: mrp - baseCost,
                        pct: mrp > 0 ? ((mrp - baseCost) / mrp) * 100 : 0,
                        testId: 'margin-mrp-base'
                      },
                      {
                        label: 'MRP (Excl GST) vs Base Cost',
                        diff: mrpExcl - baseCost,
                        pct: mrpExcl > 0 ? ((mrpExcl - baseCost) / mrpExcl) * 100 : 0,
                        testId: 'margin-mrp-excl-base'
                      }
                    ];
                    return blocks.map(b => (
                      <div key={b.label}>
                        <p className="text-xs text-gray-600">{b.label}</p>
                        <p className="text-base font-bold text-gray-800" data-testid={b.testId}>
                          {money(b.diff)} <span className="text-sm font-medium text-gray-500">({round2(b.pct)}%)</span>
                        </p>
                      </div>
                    ));
                  })()}
                </div>
                <p className="text-xs text-amber-700 mt-3">
                  Maximum head-room available before you set the markdown %.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Multi-Level Pricing */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                Multi-Level Pricing
                <Badge variant="outline">Customer Types & Stores</Badge>
              </CardTitle>
              <CardDescription>
                Level 1 is auto-calculated from the pricing mode above (non-editable). Levels 2-5 support
                % Markdown from MRP, % Markup over base cost, or a flat price &mdash; each with its own rule.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {pricingData.price_levels.map((level) => {
                const isL1 = level.level === 1;
                const mode = isL1 ? pricingData.markup_type : level.type;
                const value = isL1 ? pricingData.markup_value : level.value;
                const priceIncl = isL1 ? pricingData.selling_price : level.price;
                const m = levelMetrics(priceIncl);
                return (
                  <div
                    key={level.level}
                    className={`p-4 rounded-lg border ${isL1 ? 'bg-indigo-50 border-indigo-200' : 'bg-gray-50 border-gray-200'}`}
                    data-testid={`price-level-${level.level}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Badge variant={isL1 ? 'default' : 'secondary'}>Level {level.level}</Badge>
                        <Input
                          value={level.name}
                          onChange={(e) => updatePriceLevel(level.level, 'name', e.target.value)}
                          className="w-36 h-7 text-sm bg-white"
                          disabled={isL1}
                          data-testid={`price-level-name-${level.level}`}
                        />
                        {isL1 && <span className="text-xs text-gray-500">Auto from pricing mode &mdash; non-editable</span>}
                      </div>
                      {!isL1 && (
                        <Switch
                          checked={level.enabled}
                          onCheckedChange={(c) => updatePriceLevel(level.level, 'enabled', c)}
                          data-testid={`price-level-toggle-${level.level}`}
                        />
                      )}
                    </div>

                    {(isL1 || level.enabled) && (
                      <>
                        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                          <div>
                            <Label className="text-xs">Mode</Label>
                            <Select
                              value={mode}
                              onValueChange={(v) => updatePriceLevel(level.level, 'type', v)}
                              disabled={isL1}
                            >
                              <SelectTrigger className="h-8 bg-white" data-testid={`price-level-mode-${level.level}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="markdown">% Markdown</SelectItem>
                                <SelectItem value="percentage">% Markup</SelectItem>
                                <SelectItem value="flat">Flat Price</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs">
                              {mode === 'flat' ? 'Flat ₹' : mode === 'markdown' ? 'Markdown %' : 'Markup %'}
                            </Label>
                            <Input
                              type="number"
                              value={value}
                              onChange={(e) => updatePriceLevel(level.level, 'value', parseFloat(e.target.value) || 0)}
                              disabled={isL1}
                              className="h-8 bg-white"
                              data-testid={`price-level-value-${level.level}`}
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Price (Excl. Tax)</Label>
                            <div className="h-8 px-3 flex items-center bg-white border rounded-md text-sm text-gray-700"
                                 data-testid={`price-level-excl-${level.level}`}>
                              {money(m.priceExcl)}
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs">Price (Incl. Tax)</Label>
                            <div className="h-8 px-3 flex items-center bg-emerald-50 border border-emerald-200 rounded-md text-sm font-semibold text-emerald-700"
                                 data-testid={`price-level-incl-${level.level}`}>
                              {money(priceIncl)}
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs">Profit (Excl. Tax)</Label>
                            <div className={`h-8 px-3 flex items-center border rounded-md text-sm font-semibold ${m.profit >= 0 ? 'bg-white text-gray-800' : 'bg-red-50 border-red-200 text-red-600'}`}
                                 data-testid={`price-level-profit-${level.level}`}>
                              {money(m.profit)}
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs">GST @ {gstRate}%</Label>
                            <div className="h-8 px-3 flex items-center bg-white border rounded-md text-sm text-gray-600">
                              {money(priceIncl - m.priceExcl)}
                            </div>
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-2 mt-3 text-xs">
                          <span className="px-2 py-1 rounded bg-emerald-100 text-emerald-800" data-testid={`price-level-margin-${level.level}`}>
                            Margin: {m.marginPct}%
                          </span>
                          <span className="px-2 py-1 rounded bg-blue-100 text-blue-800" data-testid={`price-level-markup-${level.level}`}>
                            Markup: {m.markupPct}%
                          </span>
                          <span className="px-2 py-1 rounded bg-amber-100 text-amber-800" data-testid={`price-level-markdown-${level.level}`}>
                            Markdown: {m.markdownPct}% (off MRP {money(pricingData.mrp)})
                          </span>
                          {priceIncl > (parseFloat(pricingData.mrp) || 0) && (
                            <span className="px-2 py-1 rounded bg-red-100 text-red-700">Price is above MRP</span>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}

              <div className="mt-2 p-3 bg-blue-50 rounded-lg flex gap-2 text-sm text-blue-700">
                <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <p>
                  Margin = profit ÷ price (excl. tax) · Markup = profit ÷ base cost · Markdown = discount off MRP.
                  Profit compares the excl.-tax price against the base cost (Auto GRN).
                </p>
              </div>
            </CardContent>
          </Card>
          </fieldset>
        </TabsContent>

        {/* INVENTORY Tab */}
        <TabsContent value="inventory" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Stock Levels</CardTitle>
                <CardDescription>Configure reorder points and stock alerts</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Minimum Stock Level</Label>
                    <Input
                      type="number"
                      value={inventoryData.min_stock_level}
                      onChange={(e) => setInventoryData(prev => ({ ...prev, min_stock_level: parseFloat(e.target.value) || 0 }))}
                      data-testid="input-min-stock"
                    />
                    <p className="text-xs text-gray-500 mt-1">Alert when stock falls below this</p>
                  </div>
                  <div>
                    <Label>Maximum Stock Level</Label>
                    <Input
                      type="number"
                      value={inventoryData.max_stock_level}
                      onChange={(e) => setInventoryData(prev => ({ ...prev, max_stock_level: parseFloat(e.target.value) || 0 }))}
                    />
                    <p className="text-xs text-gray-500 mt-1">Optimal storage capacity</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Reorder Level</Label>
                    <Input
                      type="number"
                      value={inventoryData.reorder_level}
                      onChange={(e) => setInventoryData(prev => ({ ...prev, reorder_level: parseFloat(e.target.value) || 0 }))}
                    />
                    <p className="text-xs text-gray-500 mt-1">Trigger purchase order</p>
                  </div>
                  <div>
                    <Label>Low Stock Alert Qty</Label>
                    <Input
                      type="number"
                      value={inventoryData.low_stock_alert_qty}
                      onChange={(e) => setInventoryData(prev => ({ ...prev, low_stock_alert_qty: parseFloat(e.target.value) || 0 }))}
                    />
                    <p className="text-xs text-gray-500 mt-1">Show warning at this level</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {isEdit && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Current Stock Status</CardTitle>
                  <CardDescription>Stock levels across locations</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-8 text-gray-400">
                    <Boxes className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Stock information will be displayed here</p>
                    <p className="text-sm">Check the Inventory module for detailed stock levels</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Pricing unlock dialog */}
        <Dialog open={unlockDialogOpen} onOpenChange={setUnlockDialogOpen}>
          <DialogContent className="bg-white max-w-md" data-testid="pricing-unlock-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-700">
                <KeyRound className="w-5 h-5" /> Unlock frozen pricing
              </DialogTitle>
              <DialogDescription>
                Enter the active Super Admin security code (HQ Settings - Security Code). The unlock and any
                margin change are logged.
              </DialogDescription>
            </DialogHeader>
            <Input
              value={pricingUnlockCode}
              onChange={(e) => setPricingUnlockCode(e.target.value.toUpperCase())}
              placeholder="6-character code"
              maxLength={6}
              className="font-mono tracking-[0.3em] text-center text-lg"
              data-testid="pricing-unlock-code-input"
              autoFocus
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => setUnlockDialogOpen(false)}>Cancel</Button>
              <Button
                className="bg-red-600 hover:bg-red-700"
                disabled={pricingUnlockCode.length < 6 || unlocking}
                onClick={async () => {
                  setUnlocking(true);
                  try {
                    await securityAPI.validate({
                      code: pricingUnlockCode,
                      purpose: 'product_pricing_unlock',
                      context: `${generalData.sku} - ${generalData.name}`
                    });
                    setPricingUnlocked(true);
                    setUnlockDialogOpen(false);
                    toast({ title: 'Pricing unlocked', description: 'You can now edit pricing and margins' });
                  } catch (error) {
                    toast({
                      title: 'Invalid code',
                      description: error.response?.data?.detail || 'The security code is invalid or expired',
                      variant: 'destructive'
                    });
                  } finally {
                    setUnlocking(false);
                  }
                }}
                data-testid="pricing-unlock-submit"
              >
                Unlock
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* IMAGES Tab */}
        <TabsContent value="images" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Product Images</CardTitle>
              <CardDescription>Upload JPG, JPEG, PNG or WEBP files &mdash; max 3MB per image</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Primary Image */}
                <div>
                  <Label className="mb-2 block">Primary Image</Label>
                  <input
                    type="file"
                    ref={primaryImageInputRef}
                    accept={IMAGE_ACCEPT}
                    onChange={handlePrimaryImageUpload}
                    className="hidden"
                    data-testid="primary-image-file-input"
                  />
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                    {imagesData.primary_image ? (
                      <div className="relative">
                        <img
                          src={imagesData.primary_image}
                          alt="Primary"
                          className="max-h-48 mx-auto rounded object-contain"
                          data-testid="primary-image-preview"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'block';
                          }}
                        />
                        <div className="hidden text-center py-4">
                          <AlertCircle className="w-8 h-8 mx-auto text-red-400 mb-2" />
                          <p className="text-sm text-red-500">Failed to load image</p>
                        </div>
                        <Button
                          variant="destructive"
                          size="sm"
                          className="absolute top-2 right-2"
                          onClick={() => setImagesData(prev => ({ ...prev, primary_image: '' }))}
                          data-testid="remove-primary-image-btn"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="py-8">
                        {uploadingPrimary ? (
                          <>
                            <Loader2 className="w-12 h-12 mx-auto text-indigo-500 mb-2 animate-spin" />
                            <p className="text-sm text-indigo-600">Uploading...</p>
                          </>
                        ) : (
                          <>
                            <Upload className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                            <p className="text-sm text-gray-500 mb-1">Click to upload the primary image</p>
                            <p className="text-xs text-gray-400 mb-3">JPG, JPEG, PNG, WEBP &middot; max 3MB</p>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => primaryImageInputRef.current?.click()}
                              disabled={uploadingPrimary}
                              data-testid="upload-primary-image-btn"
                            >
                              <Upload className="w-4 h-4 mr-2" />
                              Choose File
                            </Button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  {imagesData.primary_image && !uploadingPrimary && (
                    <div className="mt-2 flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => primaryImageInputRef.current?.click()}
                        disabled={uploadingPrimary}
                        data-testid="replace-primary-image-btn"
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Replace Image
                      </Button>
                    </div>
                  )}
                </div>

                {/* Additional Images */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label>Additional Images</Label>
                    <div className="flex gap-2">
                      <input
                        type="file"
                        ref={additionalImageInputRef}
                        accept={IMAGE_ACCEPT}
                        multiple
                        onChange={handleAdditionalImageUpload}
                        className="hidden"
                        data-testid="additional-image-file-input"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => additionalImageInputRef.current?.click()}
                        disabled={uploadingAdditional}
                        data-testid="upload-additional-image-btn"
                      >
                        {uploadingAdditional ? (
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        ) : (
                          <Upload className="w-4 h-4 mr-1" />
                        )}
                        Upload Files
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-2 max-h-80 overflow-y-auto" data-testid="additional-images-list">
                    {imagesData.additional_images.map((url, i) => (
                      <div key={i} className="flex gap-3 items-center p-2 bg-gray-50 rounded">
                        <img
                          src={url}
                          alt={`Additional ${i + 1}`}
                          className="w-12 h-12 object-cover rounded border"
                          data-testid={`additional-image-preview-${i}`}
                          onError={(e) => { e.target.style.visibility = 'hidden'; }}
                        />
                        <span className="flex-1 text-xs text-gray-500 truncate">{url}</span>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setImagesData(prev => ({
                              ...prev,
                              additional_images: prev.additional_images.filter((_, idx) => idx !== i)
                            }));
                          }}
                          data-testid={`remove-additional-image-${i}`}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    ))}
                    {imagesData.additional_images.length === 0 && (
                      <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
                        <ImageIcon className="w-8 h-8 mx-auto text-gray-300 mb-2" />
                        <p className="text-sm text-gray-400">No additional images</p>
                        <p className="text-xs text-gray-400 mt-1">JPG, JPEG, PNG, WEBP &middot; max 3MB each</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ProductEditPage;
